import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserProfile as UserProfileType, UserProfileUpdate, UserFavorite, Review } from '../types';
import { userProfileApi, favoritesApi, reviewsApi } from '../services/api';
import { User, Heart, Star, Settings, MapPin, Mail, Shield } from 'lucide-react';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling
import { hasRole } from '../utils/rbac';
import EmailPreferences from './EmailPreferences'; // Issue #1265
import AnalyticsPrivacySettings from './AnalyticsPrivacySettings'; // Issue #1310

type TabType = 'overview' | 'favorites' | 'reviews' | 'email-preferences' | 'analytics-privacy';

export default function UserProfile() {
  const [profile, setProfile] = useState<UserProfileType | null>(null);
  const [favorites, setFavorites] = useState<UserFavorite[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [formData, setFormData] = useState<UserProfileUpdate>({});

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const [profileData, favoritesData, reviewsData] = await Promise.all([
        userProfileApi.getProfile(),
        favoritesApi.getFavorites(),
        reviewsApi.getUserReviews(),
      ]);

      setProfile(profileData);
      setFavorites(favoritesData);
      setReviews(reviewsData);

      // Initialize form data with current profile
      setFormData({
        display_name: profileData.display_name,
        bio: profileData.bio,
        avatar_url: profileData.avatar_url,
        email_notifications: profileData.email_notifications,
        review_notifications: profileData.review_notifications,
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load profile:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const updatedProfile = await userProfileApi.updateProfile(formData);
      setProfile(updatedProfile);
      setEditing(false);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to update profile:', apiError);
      setError(apiError.message);
    }
  };

  const handleCancelEdit = () => {
    if (profile) {
      setFormData({
        display_name: profile.display_name,
        bio: profile.bio,
        avatar_url: profile.avatar_url,
        email_notifications: profile.email_notifications,
        review_notifications: profile.review_notifications,
      });
    }
    setEditing(false);
    setError(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Loading profile...</div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={loadProfile}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-teal-200 rounded-full flex items-center justify-center">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.display_name || profile.email.split('@')[0]}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <User className="h-10 w-10 text-secondary" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {profile?.display_name || profile?.email.split('@')[0]}
              </h1>
              <p className="text-gray-600">{profile?.email}</p>
              {profile?.created_at && (
                <p className="text-sm text-gray-500 mt-1">
                  Member since{' '}
                  {new Date(profile.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              )}
              {hasRole('Admin') && (
                <span className="inline-block mt-1 px-2 py-1 bg-secondary-light text-primary text-xs font-semibold rounded">
                  Admin
                </span>
              )}
              {hasRole('Fair Manager') && (
                <span className="inline-block mt-1 ml-2 px-2 py-1 bg-secondary-light text-primary text-xs font-semibold rounded">
                  Fair Manager
                </span>
              )}
              {profile?.is_early_access && (
                <span className="inline-block mt-1 ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded">
                  ⭐ Early Access
                </span>
              )}
            </div>
          </div>

          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
            >
              <Settings className="h-4 w-4" />
              <span>Edit Profile</span>
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                id="display_name"
                type="text"
                value={formData.display_name || ''}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter your display name"
              />
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                Bio
              </label>
              <textarea
                id="bio"
                value={formData.bio || ''}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
                placeholder="Tell us about yourself..."
              />
            </div>

            <div>
              <label htmlFor="avatar_url" className="block text-sm font-medium text-gray-700 mb-1">
                Avatar URL
              </label>
              <input
                id="avatar_url"
                type="url"
                value={formData.avatar_url || ''}
                onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="https://example.com/avatar.jpg"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.email_notifications ?? true}
                  onChange={(e) => setFormData({ ...formData, email_notifications: e.target.checked })}
                  className="rounded text-secondary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Email notifications</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.review_notifications ?? true}
                  onChange={(e) => setFormData({ ...formData, review_notifications: e.target.checked })}
                  className="rounded text-secondary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Review notifications</span>
              </label>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            {profile?.bio && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Bio</h3>
                <p className="mt-1 text-gray-900">{profile.bio}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent Reviews Section */}
      {reviews.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Reviews</h2>
          <div className="space-y-4">
            {reviews.slice(-5).reverse().map((review) => (
              <div
                key={review.id}
                className="border-b border-gray-200 pb-4 last:border-0 last:pb-0"
              >
                <div className="flex items-center space-x-2 mb-1">
                  <div
                    className="flex text-yellow-400"
                    role="img"
                    aria-label={`${review.rating} out of 5 stars`}
                  >
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${i < review.rating ? 'fill-current' : ''}`}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">
                    {review.created_at
                      ? new Date(review.created_at).toLocaleDateString()
                      : ''}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900">{review.title}</h3>
                <p className="mt-1 text-gray-600 text-sm">{review.comment}</p>
                {review.fair_id && (
                  <Link
                    to={`/fair/${review.fair_id}`}
                    className="text-sm text-secondary hover:text-primary-dark font-medium mt-2 inline-block"
                  >
                    View Fair →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {reviews.length === 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Reviews</h2>
          <div className="text-center py-8">
            <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">You haven't written any reviews yet.</p>
            <Link
              to="/"
              className="text-secondary hover:text-primary-dark font-medium mt-2 inline-block"
            >
              Explore Artist Fairs →
            </Link>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'overview'
                  ? 'border-primary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4" />
                <span>Overview</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('favorites')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'favorites'
                  ? 'border-primary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Heart className="h-4 w-4" />
                <span>Favorites ({favorites.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'reviews'
                  ? 'border-primary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Star className="h-4 w-4" />
                <span>Reviews ({reviews.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('email-preferences')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'email-preferences'
                  ? 'border-primary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <span>Email Preferences</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('analytics-privacy')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'analytics-privacy'
                  ? 'border-primary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Shield className="h-4 w-4" />
                <span>Analytics & Privacy</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-teal-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Heart className="h-6 w-6 text-red-500" />
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{favorites.length}</div>
                      <div className="text-sm text-gray-600">Favorites</div>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Star className="h-6 w-6 text-yellow-500" />
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{reviews.length}</div>
                      <div className="text-sm text-gray-600">Reviews</div>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <User className="h-6 w-6 text-secondary" />
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {profile?.created_at ? new Date(profile.created_at).getFullYear() : 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600">Member Since</div>
                    </div>
                  </div>
                </div>
              </div>

              {reviews.length > 0 && (
                <div>
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Recent Activity</h2>
                  <div className="space-y-4">
                    {reviews.slice(0, 3).map((review) => (
                      <div key={review.id} className="border-b border-gray-200 pb-4 last:border-0 last:pb-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <div className="flex text-yellow-400">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${i < review.rating ? 'fill-current' : ''}`}
                              />
                            ))}
                          </div>
                          <span className="text-sm text-gray-500">
                            {review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{review.title}</h3>
                        <p className="mt-1 text-gray-600 text-sm">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Favorites Tab */}
          {activeTab === 'favorites' && (
            <div>
              {favorites.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No favorites yet</h3>
                  <p className="text-gray-500 mb-4">Start exploring artist fairs and save your favorites!</p>
                  <Link to="/" className="text-secondary hover:text-primary-dark font-medium">
                    Browse Artist Fairs →
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {favorites.map((fav) => (
                    <Link
                      key={fav.id}
                      to={`/fair/${fav.fair_id}`}
                      className="block border border-gray-200 rounded-lg p-4 hover:border-primary hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{fav.fair_name}</h3>
                          {fav.fair_city && fav.fair_state && (
                            <div className="flex items-center text-sm text-gray-600 mb-2">
                              <MapPin className="h-3 w-3 mr-1" />
                              {fav.fair_city}, {fav.fair_state}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            Added {fav.created_at ? new Date(fav.created_at).toLocaleDateString() : ''}
                          </div>
                        </div>
                        <Heart className="h-5 w-5 text-red-500 fill-current flex-shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div>
              {reviews.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                  <p className="text-gray-500 mb-4">Share your experiences with the community!</p>
                  <Link to="/" className="text-secondary hover:text-primary-dark font-medium">
                    Visit Artist Fairs →
                  </Link>
                </div>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{review.title}</h3>
                          <div className="flex items-center space-x-2">
                            <div className="flex text-yellow-400">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${i < review.rating ? 'fill-current' : ''}`}
                                />
                              ))}
                            </div>
                            <span className="text-sm text-gray-500">
                              {review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-3">{review.comment}</p>
                      {review.fair_id && (
                        <Link
                          to={`/fair/${review.fair_id}`}
                          className="text-sm text-secondary hover:text-primary-dark font-medium"
                        >
                          View Artist Fair →
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Email Preferences Tab (Issue #1265) */}
          {activeTab === 'email-preferences' && (
            <div>
              <EmailPreferences />
            </div>
          )}

          {/* Analytics & Privacy Tab (Issue #1310) */}
          {activeTab === 'analytics-privacy' && (
            <div>
              <AnalyticsPrivacySettings />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
